/**
 * RealLifeCalcPage.tsx — 실생활 일차함수 계산기
 *
 * 시나리오 5가지:
 *  1. 택시 요금 계산기     y = 기본요금 + 요금/km × x
 *  2. 적금/저축 계산기     y = 초기금액 + 월저축 × x
 *  3. 휴대폰 요금제 비교   y = 기본료 + 단가 × x
 *  4. 양초 녹는 시간       y = 초기길이 - 녹는속도 × x
 *  5. 자동차 연료          y = 초기연료 - 연비역수 × x
 */

import React, { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Car, PiggyBank, Smartphone, Flame, Fuel,
  ArrowLeft, ChevronRight, Calculator, Info,
  TrendingUp, TrendingDown
} from 'lucide-react'

// ── 타입 ─────────────────────────────────────────────────────────
interface Param {
  key: string
  label: string
  unit: string
  defaultVal: number
  min: number
  max: number
  step: number
  desc: string        // 수학적 의미 설명
}

interface Scenario {
  id: string
  icon: React.ElementType
  emoji: string
  title: string
  subtitle: string
  color: { from: string; to: string; text: string; light: string; border: string }
  params: Param[]
  // y = a*x + b 형태로 계산
  formula: (params: Record<string, number>, x: number) => number
  formulaLabel: (params: Record<string, number>) => string  // 수식 문자열
  xLabel: string       // x축 의미
  xUnit: string
  yLabel: string       // y축 의미
  yUnit: string
  xMax: number
  xStep: number
  insight: (params: Record<string, number>, x: number, y: number) => string  // 실생활 해석
  mathPoint: string    // 연결된 수학 개념 설명
}

// ── 시나리오 정의 ────────────────────────────────────────────────
const SCENARIOS: Scenario[] = [
  {
    id: 'taxi',
    icon: Car,
    emoji: '🚕',
    title: '택시 요금',
    subtitle: '거리에 따른 요금을 계산해요',
    color: { from: '#f59e0b', to: '#d97706', text: 'text-amber-700', light: 'bg-amber-50', border: 'border-amber-200' },
    params: [
      { key: 'base', label: '기본요금', unit: '원', defaultVal: 4800, min: 1000, max: 10000, step: 100, desc: 'y절편 (b): x=0일 때 요금' },
      { key: 'rate', label: 'km당 요금', unit: '원/km', defaultVal: 1000, min: 100, max: 3000, step: 100, desc: '기울기 (a): 1km 증가 시 요금 증가량' },
    ],
    formula: (p, x) => p.base + p.rate * x,
    formulaLabel: (p) => `y = ${p.rate.toLocaleString()}x + ${p.base.toLocaleString()}`,
    xLabel: '이동 거리', xUnit: 'km', yLabel: '택시 요금', yUnit: '원',
    xMax: 30, xStep: 1,
    insight: (p, x, y) => `${x}km 이동 시 요금은 ${y.toLocaleString()}원이에요. 기본요금 ${p.base.toLocaleString()}원 + 거리요금 ${(p.rate * x).toLocaleString()}원`,
    mathPoint: '기본요금이 y절편(b), km당 요금이 기울기(a)예요. y = ax + b 형태 그대로입니다!',
  },
  {
    id: 'savings',
    icon: PiggyBank,
    emoji: '🐷',
    title: '저축 계산기',
    subtitle: '매달 저축하면 얼마가 될까요?',
    color: { from: '#10b981', to: '#059669', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200' },
    params: [
      { key: 'initial', label: '초기 저축액', unit: '원', defaultVal: 100000, min: 0, max: 1000000, step: 10000, desc: 'y절편 (b): 시작 금액' },
      { key: 'monthly', label: '월 저축액', unit: '원/월', defaultVal: 50000, min: 1000, max: 500000, step: 1000, desc: '기울기 (a): 매월 증가량' },
    ],
    formula: (p, x) => p.initial + p.monthly * x,
    formulaLabel: (p) => `y = ${p.monthly.toLocaleString()}x + ${p.initial.toLocaleString()}`,
    xLabel: '저축 기간', xUnit: '개월', yLabel: '총 저축액', yUnit: '원',
    xMax: 24, xStep: 1,
    insight: (p, x, y) => `${x}개월 후 총 ${y.toLocaleString()}원! 원금 ${p.initial.toLocaleString()}원 + 저축 ${(p.monthly * x).toLocaleString()}원`,
    mathPoint: '초기 저축액이 y절편(b), 월 저축액이 기울기(a)예요. 기울기가 클수록 통장이 빨리 늘어나요!',
  },
  {
    id: 'phone',
    icon: Smartphone,
    emoji: '📱',
    title: '요금제 비교',
    subtitle: '어느 요금제가 더 유리할까요?',
    color: { from: '#6366f1', to: '#4f46e5', text: 'text-indigo-700', light: 'bg-indigo-50', border: 'border-indigo-200' },
    params: [
      { key: 'base', label: '기본료', unit: '원', defaultVal: 10000, min: 0, max: 50000, step: 1000, desc: 'y절편 (b): 사용량 0일 때 요금' },
      { key: 'rate', label: 'GB당 요금', unit: '원/GB', defaultVal: 3000, min: 500, max: 10000, step: 500, desc: '기울기 (a): 데이터 1GB당 추가 요금' },
      { key: 'base2', label: '비교 요금제 기본료', unit: '원', defaultVal: 30000, min: 0, max: 80000, step: 1000, desc: '두 번째 요금제 y절편' },
      { key: 'rate2', label: '비교 요금제 GB당 요금', unit: '원/GB', defaultVal: 1000, min: 0, max: 5000, step: 500, desc: '두 번째 요금제 기울기' },
    ],
    formula: (p, x) => p.base + p.rate * x,
    formulaLabel: (p) => `요금제A: y = ${p.rate.toLocaleString()}x + ${p.base.toLocaleString()} | 요금제B: y = ${p.rate2.toLocaleString()}x + ${p.base2.toLocaleString()}`,
    xLabel: '데이터 사용량', xUnit: 'GB', yLabel: '월 요금', yUnit: '원',
    xMax: 20, xStep: 1,
    insight: (p, x, y) => {
      const y2 = p.base2 + p.rate2 * x
      const diff = y - y2
      // 교점 계산: base + rate*x = base2 + rate2*x → x = (base2-base)/(rate-rate2)
      const crossX = p.rate !== p.rate2 ? (p.base2 - p.base) / (p.rate - p.rate2) : null
      if (diff > 0) return `${x}GB 사용 시: A요금제 ${y.toLocaleString()}원, B요금제 ${y2.toLocaleString()}원 → B가 ${Math.abs(diff).toLocaleString()}원 저렴${crossX !== null && crossX > 0 ? ` (${crossX.toFixed(1)}GB 이상이면 B가 유리)` : ''}`
      if (diff < 0) return `${x}GB 사용 시: A요금제 ${y.toLocaleString()}원, B요금제 ${y2.toLocaleString()}원 → A가 ${Math.abs(diff).toLocaleString()}원 저렴${crossX !== null && crossX > 0 ? ` (${crossX.toFixed(1)}GB 이하면 A가 유리)` : ''}`
      return `${x}GB에서 두 요금제 요금이 같아요! (${y.toLocaleString()}원)`
    },
    mathPoint: '두 일차함수의 교점이 "어느 요금제가 유리한가"의 기준점이에요. 연립방정식으로 교점을 구해보세요!',
  },
  {
    id: 'candle',
    icon: Flame,
    emoji: '🕯️',
    title: '양초 타는 시간',
    subtitle: '양초가 얼마나 남았을까요?',
    color: { from: '#ec4899', to: '#db2777', text: 'text-pink-700', light: 'bg-pink-50', border: 'border-pink-200' },
    params: [
      { key: 'height', label: '초기 양초 길이', unit: 'cm', defaultVal: 20, min: 5, max: 50, step: 1, desc: 'y절편 (b): 시작 길이' },
      { key: 'rate', label: '시간당 녹는 길이', unit: 'cm/시간', defaultVal: 2, min: 0.5, max: 10, step: 0.5, desc: '기울기의 절댓값 (|a|): 감소 속도' },
    ],
    formula: (p, x) => Math.max(0, p.height - p.rate * x),
    formulaLabel: (p) => `y = -${p.rate}x + ${p.height}`,
    xLabel: '경과 시간', xUnit: '시간', yLabel: '남은 양초 길이', yUnit: 'cm',
    xMax: 20, xStep: 0.5,
    insight: (p, x, y) => {
      const burnTime = p.height / p.rate
      if (y <= 0) return `양초가 다 탔어요! 총 ${burnTime.toFixed(1)}시간 만에 다 탑니다.`
      return `${x}시간 후 ${y.toFixed(1)}cm 남아요. 총 ${burnTime.toFixed(1)}시간 후에 다 타요!`
    },
    mathPoint: '기울기가 음수(-a)예요! 감소하는 일차함수는 기울기 < 0 입니다. y=0이 되는 x값이 "다 타는 시간"이에요.',
  },
  {
    id: 'fuel',
    icon: Fuel,
    emoji: '⛽',
    title: '자동차 연료',
    subtitle: '기름이 얼마나 남았을까요?',
    color: { from: '#0ea5e9', to: '#0284c7', text: 'text-sky-700', light: 'bg-sky-50', border: 'border-sky-200' },
    params: [
      { key: 'tank', label: '초기 연료량', unit: 'L', defaultVal: 50, min: 10, max: 100, step: 1, desc: 'y절편 (b): 처음 연료' },
      { key: 'mpg', label: '연비', unit: 'km/L', defaultVal: 12, min: 5, max: 30, step: 1, desc: '기울기 계산에 사용: 1km당 1/연비 L 소모' },
    ],
    formula: (p, x) => Math.max(0, p.tank - x / p.mpg),
    formulaLabel: (p) => `y = -${(1 / p.mpg).toFixed(3)}x + ${p.tank}`,
    xLabel: '주행 거리', xUnit: 'km', yLabel: '남은 연료', yUnit: 'L',
    xMax: 600, xStep: 10,
    insight: (p, x, y) => {
      const maxDist = p.tank * p.mpg
      if (y <= 0) return `연료가 바닥났어요! 최대 ${maxDist.toFixed(0)}km까지 갈 수 있어요.`
      return `${x}km 주행 후 ${y.toFixed(1)}L 남음. 앞으로 ${(y * p.mpg).toFixed(0)}km 더 갈 수 있어요!`
    },
    mathPoint: '연비(km/L)가 클수록 기울기의 절댓값이 작아요. 즉 완만한 감소 = 연비 좋음! y=0일 때 x가 최대 주행 거리예요.',
  },
]

// ── 그래프 SVG 컴포넌트 ──────────────────────────────────────────
function MiniGraph({
  scenario,
  params,
  xVal,
  color,
}: {
  scenario: Scenario
  params: Record<string, number>
  xVal: number
  color: { from: string; to: string }
}) {
  const W = 280, H = 160, PAD = 32

  const points: { x: number; y: number }[] = []
  const steps = 40
  for (let i = 0; i <= steps; i++) {
    const xv = (scenario.xMax / steps) * i
    const yv = scenario.formula(params, xv)
    points.push({ x: xv, y: yv })
  }

  const yMax = Math.max(...points.map(p => p.y), 1)
  const yMin = Math.min(...points.map(p => p.y), 0)
  const yRange = yMax - yMin || 1

  const toSvg = (xv: number, yv: number) => ({
    sx: PAD + ((xv / scenario.xMax) * (W - PAD * 2)),
    sy: (H - PAD) - ((yv - yMin) / yRange) * (H - PAD * 2),
  })

  const pathD = points.map((p, i) => {
    const { sx, sy } = toSvg(p.x, p.y)
    return `${i === 0 ? 'M' : 'L'}${sx},${sy}`
  }).join(' ')

  const currentY = scenario.formula(params, xVal)
  const { sx: cx, sy: cy } = toSvg(xVal, currentY)

  // 요금제 비교인 경우 두 번째 선
  const isPhone = scenario.id === 'phone'
  const path2D = isPhone ? points.map((p, i) => {
    const y2 = params.base2 + params.rate2 * p.x
    const { sx, sy } = toSvg(p.x, Math.max(y2, yMin))
    return `${i === 0 ? 'M' : 'L'}${sx},${sy}`
  }).join(' ') : null

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full">
      {/* 배경 그리드 */}
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const sy = (H - PAD) - t * (H - PAD * 2)
        return <line key={t} x1={PAD} y1={sy} x2={W - PAD} y2={sy} stroke="#e2e8f0" strokeWidth={1} />
      })}
      {/* 축 */}
      <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="#94a3b8" strokeWidth={1.5} />
      <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#94a3b8" strokeWidth={1.5} />

      {/* 두 번째 선 (요금제 비교) */}
      {path2D && (
        <path d={path2D} fill="none" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5,3" />
      )}

      {/* 메인 선 */}
      <defs>
        <linearGradient id={`grad-${scenario.id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color.from} />
          <stop offset="100%" stopColor={color.to} />
        </linearGradient>
      </defs>
      <path d={pathD} fill="none" stroke={`url(#grad-${scenario.id})`} strokeWidth={2.5} strokeLinecap="round" />

      {/* 현재 x 위치 수직선 */}
      <line x1={cx} y1={PAD} x2={cx} y2={H - PAD} stroke="#64748b" strokeWidth={1} strokeDasharray="3,2" />

      {/* 현재 점 */}
      <circle cx={cx} cy={cy} r={5} fill={color.from} stroke="white" strokeWidth={2} />

      {/* 축 레이블 */}
      <text x={PAD - 4} y={PAD + 2} fontSize={9} fill="#94a3b8" textAnchor="end">{yMax.toLocaleString()}</text>
      <text x={PAD - 4} y={H - PAD + 2} fontSize={9} fill="#94a3b8" textAnchor="end">{yMin.toLocaleString()}</text>
      <text x={W - PAD} y={H - PAD + 12} fontSize={9} fill="#94a3b8" textAnchor="middle">{scenario.xMax}</text>
      <text x={PAD} y={H - PAD + 12} fontSize={9} fill="#94a3b8" textAnchor="middle">0</text>

      {/* 축 이름 */}
      <text x={W / 2} y={H - 2} fontSize={9} fill="#64748b" textAnchor="middle">{scenario.xLabel} ({scenario.xUnit})</text>
      <text x={8} y={H / 2} fontSize={9} fill="#64748b" textAnchor="middle"
        transform={`rotate(-90, 8, ${H / 2})`}>{scenario.yLabel}</text>
    </svg>
  )
}

// ── 메인 페이지 ──────────────────────────────────────────────────
export default function RealLifeCalcPage() {
  const [selected, setSelected] = useState<string | null>(null)
  const [paramValues, setParamValues] = useState<Record<string, Record<string, number>>>({})
  const [xValues, setXValues] = useState<Record<string, number>>({})

  const scenario = SCENARIOS.find(s => s.id === selected) ?? null

  const getParams = useCallback((id: string) => {
    const sc = SCENARIOS.find(s => s.id === id)!
    if (paramValues[id]) return paramValues[id]
    const defaults: Record<string, number> = {}
    sc.params.forEach(p => { defaults[p.key] = p.defaultVal })
    return defaults
  }, [paramValues])

  const getX = (id: string) => xValues[id] ?? (SCENARIOS.find(s => s.id === id)?.xMax ?? 10) / 3

  const setParam = (scenarioId: string, key: string, val: number) => {
    setParamValues(prev => ({
      ...prev,
      [scenarioId]: { ...getParams(scenarioId), [key]: val },
    }))
  }

  const setX = (scenarioId: string, val: number) => {
    setXValues(prev => ({ ...prev, [scenarioId]: val }))
  }

  // ── 카드 목록 화면 ─────────────────────────────────────────────
  if (!selected) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        {/* 헤더 */}
        <div>
          <Link to="/" className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700 mb-4">
            <ArrowLeft size={15} /> 대시보드로
          </Link>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center">
              <Calculator size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-800">실생활 일차함수 계산기</h1>
              <p className="text-sm text-slate-500">수학이 실제 생활에서 어떻게 쓰이는지 직접 계산해보세요</p>
            </div>
          </div>
        </div>

        {/* 수학 개념 연결 안내 */}
        <div className="rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 p-4 flex gap-3">
          <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-blue-800 mb-0.5">왜 일차함수인가요?</p>
            <p className="text-xs text-blue-600 leading-relaxed">
              아래 모든 계산은 <strong>y = ax + b</strong> 형태예요.
              a(기울기)는 "변화 속도", b(y절편)는 "시작값"이에요.
              슬라이더를 움직이면 그래프가 어떻게 바뀌는지 직접 확인해보세요!
            </p>
          </div>
        </div>

        {/* 시나리오 카드 목록 */}
        <div className="grid grid-cols-1 gap-4">
          {SCENARIOS.map(sc => {
            const Icon = sc.icon
            const p = getParams(sc.id)
            const x = getX(sc.id)
            const y = sc.formula(p, x)
            return (
              <button
                key={sc.id}
                onClick={() => setSelected(sc.id)}
                className="w-full text-left rounded-2xl border p-5 hover:shadow-md transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] group"
                style={{ borderColor: sc.color.border.replace('border-', '') }}
              >
                <div className="flex items-center gap-4">
                  {/* 아이콘 */}
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-md"
                    style={{ background: `linear-gradient(135deg, ${sc.color.from}, ${sc.color.to})` }}
                  >
                    <span className="text-2xl">{sc.emoji}</span>
                  </div>

                  {/* 내용 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-black text-slate-800">{sc.title}</p>
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                        {sc.formulaLabel(p).split('|')[0].trim()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{sc.subtitle}</p>
                  </div>

                  {/* 화살표 */}
                  <ChevronRight size={18} className="text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                </div>
              </button>
            )
          })}
        </div>

        {/* 하단 안내 */}
        <p className="text-center text-xs text-slate-400">
          🎓 각 계산기에서 기울기·y절편·그래프를 직접 체험할 수 있어요
        </p>
      </div>
    )
  }

  // ── 개별 계산기 화면 ───────────────────────────────────────────
  const sc = scenario!
  const params = getParams(sc.id)
  const x = getX(sc.id)
  const y = sc.formula(params, x)
  const Icon = sc.icon

  // 기울기 방향 판단
  const slope = sc.id === 'candle' ? -params.rate
    : sc.id === 'fuel' ? -(1 / params.mpg)
    : (sc.params.find(p => p.key === 'rate')?.key ? params.rate ?? params.monthly : 0)
  const isIncreasing = slope >= 0

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5">
      {/* 헤더 */}
      <div>
        <button
          onClick={() => setSelected(null)}
          className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700 mb-4"
        >
          <ArrowLeft size={15} /> 다른 계산기 보기
        </button>
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-md"
            style={{ background: `linear-gradient(135deg, ${sc.color.from}, ${sc.color.to})` }}
          >
            {sc.emoji}
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-800">{sc.title}</h2>
            <p className="text-sm text-slate-500">{sc.subtitle}</p>
          </div>
        </div>
      </div>

      {/* 수식 표시 */}
      <div
        className="rounded-2xl p-4 text-white"
        style={{ background: `linear-gradient(135deg, ${sc.color.from}, ${sc.color.to})` }}
      >
        <div className="flex items-center gap-2 mb-1">
          {isIncreasing
            ? <TrendingUp size={16} className="text-white/80" />
            : <TrendingDown size={16} className="text-white/80" />}
          <p className="text-xs font-bold text-white/70">현재 수식</p>
        </div>
        <p className="text-lg font-black font-mono">{sc.formulaLabel(params)}</p>
        <p className="text-xs text-white/60 mt-1">
          {isIncreasing ? '📈 증가하는 일차함수 (기울기 > 0)' : '📉 감소하는 일차함수 (기울기 < 0)'}
        </p>
      </div>

      {/* 파라미터 슬라이더 */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5">
        <p className="text-sm font-bold text-slate-700">⚙️ 값을 바꿔보세요</p>
        {sc.params.map(param => (
          <div key={param.key}>
            <div className="flex justify-between items-center mb-2">
              <div>
                <span className="text-sm font-bold text-slate-700">{param.label}</span>
                <span className="ml-2 text-xs text-slate-400">({param.unit})</span>
              </div>
              <span
                className="text-sm font-black px-3 py-0.5 rounded-xl text-white"
                style={{ background: sc.color.from }}
              >
                {params[param.key]?.toLocaleString()} {param.unit}
              </span>
            </div>
            <input
              type="range"
              min={param.min} max={param.max} step={param.step}
              value={params[param.key] ?? param.defaultVal}
              onChange={e => setParam(sc.id, param.key, Number(e.target.value))}
              className="w-full accent-current"
              style={{ accentColor: sc.color.from }}
            />
            <div className="flex justify-between text-xs text-slate-400 mt-0.5">
              <span>{param.min.toLocaleString()}</span>
              <span className="text-xs text-slate-400 italic">{param.desc}</span>
              <span>{param.max.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>

      {/* 그래프 */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <p className="text-sm font-bold text-slate-700 mb-3">📊 그래프</p>
        <MiniGraph scenario={sc} params={params} xVal={x} color={{ from: sc.color.from, to: sc.color.to }} />

        {/* x 슬라이더 */}
        <div className="mt-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-bold text-slate-600">
              {sc.xLabel}: <span style={{ color: sc.color.from }} className="font-black">{x} {sc.xUnit}</span>
            </span>
            <span className="text-sm font-black" style={{ color: sc.color.from }}>
              {sc.yLabel}: <span>{typeof y === 'number' ? (Number.isInteger(y) ? y.toLocaleString() : y.toFixed(1)) : y} {sc.yUnit}</span>
            </span>
          </div>
          <input
            type="range"
            min={0} max={sc.xMax} step={sc.xStep}
            value={x}
            onChange={e => setX(sc.id, Number(e.target.value))}
            className="w-full"
            style={{ accentColor: sc.color.from }}
          />
          <div className="flex justify-between text-xs text-slate-400 mt-0.5">
            <span>0 {sc.xUnit}</span>
            <span>{sc.xMax} {sc.xUnit}</span>
          </div>
        </div>
      </div>

      {/* 실생활 해석 */}
      <div className={`rounded-2xl border ${sc.color.border} ${sc.color.light} p-4`}>
        <p className="text-xs font-bold text-slate-500 mb-1.5">💬 실생활 해석</p>
        <p className={`text-sm font-bold ${sc.color.text} leading-relaxed`}>
          {sc.insight(params, x, y)}
        </p>
      </div>

      {/* 수학 개념 연결 */}
      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
        <div className="flex items-start gap-2">
          <span className="text-base">🧠</span>
          <div>
            <p className="text-xs font-bold text-blue-700 mb-1">수학 개념 연결</p>
            <p className="text-sm text-blue-700 leading-relaxed">{sc.mathPoint}</p>
          </div>
        </div>
      </div>

      {/* 학습으로 이동 CTA */}
      <Link
        to="/unit/F2-1"
        className="flex items-center justify-between w-full rounded-2xl p-4 text-white shadow-lg hover:opacity-90 transition-opacity"
        style={{ background: `linear-gradient(135deg, ${sc.color.from}, ${sc.color.to})` }}
      >
        <div>
          <p className="font-black text-sm">이 내용 제대로 배우러 가기</p>
          <p className="text-xs text-white/70 mt-0.5">1차 함수 개념 학습 → XP 획득</p>
        </div>
        <ChevronRight size={20} className="text-white/80" />
      </Link>
    </div>
  )
}
